// Family-aware syndrome detection (pure). Operates on family-level evidence
// (multiple affected relatives + confirmed gene carriers), distinct from
// Phenomizer's individual-phenotype Profile Match. Criteria live as data in
// syndromes.criteria.js.

import { CONFIDENCE } from '../constants';
import { SYNDROME_CRITERIA } from './syndromes.criteria';
import { affectedList } from './inheritance.helpers';

// pid of any family member carrying one of the syndrome's genes with an
// asserted carrier status, else null.
function familyGeneCarrier(ped, genes) {
  const set = new Set(genes.map((g) => g.toUpperCase()));
  for (const ind of ped.individuals)
    for (const g of ind.genotypes)
      if (g.gene && set.has(g.gene.toUpperCase()) && g.carrierStatus)
        return ind.pid;
  return null;
}

function detectOne(ped, crit) {
  const matchDiseases = ped.familyDiseases.filter((d) =>
    crit.diseasePatterns.some((re) => re.test(d.name || ''))
  );
  if (!matchDiseases.length) return null;

  const affectedPids = new Set();
  for (const d of matchDiseases)
    for (const ind of affectedList(ped, d.id)) affectedPids.add(ind.pid);

  const carrierPid = familyGeneCarrier(ped, crit.genes);
  const rationale = [];
  let confidence;
  const evidenceTypes = ['phenotype'];

  if (carrierPid && affectedPids.size >= 1) {
    confidence = CONFIDENCE.strong;
    rationale.push('Confirmed gene carrier with affected relative(s)');
    evidenceTypes.push('variant', 'family_pattern');
  } else if (affectedPids.size >= crit.minAffectedForStrong) {
    confidence = CONFIDENCE.strong;
    rationale.push(`${affectedPids.size} affected relatives`);
    evidenceTypes.push('family_pattern');
  } else if (affectedPids.size === 1) {
    confidence = CONFIDENCE.moderate;
    rationale.push(
      'Single affected relative — insufficient for family pattern alone'
    );
  } else {
    return null;
  }

  const supportingIds = [...affectedPids];
  if (carrierPid && !supportingIds.includes(carrierPid))
    supportingIds.push(carrierPid);

  return {
    id: crit.id,
    label: crit.label,
    omim: crit.omim,
    confidence,
    evidenceTypes,
    supportingIds,
    rationale,
    citedGuidelines: [crit.guideline],
    relatedDiseaseIds: matchDiseases.map((d) => d.id),
    relatedGenes: crit.genes,
    ruledOut: false,
    ruledOutReason: null
  };
}

export function detectSyndromes(ped) {
  return SYNDROME_CRITERIA.map((c) => detectOne(ped, c)).filter(Boolean);
}
